import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { customersApi, redstorApi } from '../services/api';
import './Redstor.css';

/**
 * Redstor (cloud backup) - protected data / seats per customer company.
 * Data arrives three ways and lands in the same cache: the RedAPI (key pasted
 * below), a CSV export from the RedApp, or typed in by hand. Map each company
 * once; every mapped product becomes a "Redstor <product> - <metric>" line in
 * Invoice Prep with the bill-by figure (GB / seats / users / workloads) as its
 * quantity. Prices live in the price book.
 */

interface Product {
  product_key: string; product_name: string | null; sku: string | null; is_trial: boolean;
  gb: number; seats: number; inactive_seats: number; users: number; workloads: number;
  metric: string; quantity: number; source: string; in_console: boolean; as_of: string | null;
}
interface Row {
  company_id: string; name: string; source: 'api' | 'csv' | 'manual'; is_enabled: boolean; in_console: boolean;
  customer_id: number | null; customer_name: string | null; products: Product[];
  total_gb: number; total_seats: number; billed_quantity: number | null; last_synced: string | null;
}
interface SimpleCustomer { id: number; xero_name: string; }
interface Config { source: 'stored' | 'env' | null; url: string; auth: string; token_hint: string | null; saved_at: string | null; }

const METRICS: { v: string; label: string }[] = [
  { v: 'gb', label: 'GB protected' }, { v: 'seats', label: 'Seats' }, { v: 'users', label: 'Users' },
  { v: 'workloads', label: 'Workloads' }, { v: 'none', label: 'Not billed' },
];
const HIDDEN_KEY = 'cv-redstor-hidden-companies';
const loadHidden = (): Set<string> => { try { return new Set(JSON.parse(localStorage.getItem(HIDDEN_KEY) || '[]')); } catch { return new Set(); } };
const saveHidden = (s: Set<string>) => { try { localStorage.setItem(HIDDEN_KEY, JSON.stringify(Array.from(s))); } catch { /* ignore */ } };
const timeAgo = (iso: string | null | undefined) => {
  if (!iso) return 'never';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
};

function CustomerPicker({ value, valueName, customers, disabled, onSelect, onClear }: {
  value: number | null; valueName: string | null; customers: SimpleCustomer[]; disabled: boolean;
  onSelect: (id: number) => void; onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (needle ? customers.filter(c => c.xero_name.toLowerCase().includes(needle)) : customers).slice(0, 40);
  }, [q, customers]);
  useEffect(() => { setIdx(0); }, [q]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const pick = (c: SimpleCustomer) => { onSelect(c.id); setOpen(false); setQ(''); };
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (matches[idx]) pick(matches[idx]); }
    else if (e.key === 'Escape') { setOpen(false); setQ(''); }
  };
  return (
    <div className={`rd-picker ${value ? 'has-value' : ''}`} ref={boxRef}>
      <input className="rd-picker-input" placeholder={value ? valueName || `#${value}` : 'Type to match a customer…'} value={q}
             disabled={disabled} onFocus={() => setOpen(true)} onChange={e => { setQ(e.target.value); setOpen(true); }} onKeyDown={onKey}
             title={value ? `Mapped to ${valueName}` : 'Not billed - type to search'} />
      {value && !q && <button className="rd-picker-clear" disabled={disabled} onClick={onClear} title="Unmap (stop billing this company)">✕</button>}
      {open && !disabled && (
        <ul className="rd-picker-list" role="listbox">
          {matches.length === 0 && <li className="rd-picker-empty">No customer matches "{q}"</li>}
          {matches.map((c, i) => (
            <li key={c.id} role="option" aria-selected={i === idx} className={`${i === idx ? 'active' : ''} ${c.id === value ? 'current' : ''}`}
                onMouseEnter={() => setIdx(i)} onMouseDown={e => { e.preventDefault(); pick(c); }}>
              {c.xero_name}{c.id === value ? ' ✓' : ''}
            </li>
          ))}
          {customers.length > matches.length && matches.length === 40 && <li className="rd-picker-empty">Keep typing to narrow down…</li>}
        </ul>
      )}
    </div>
  );
}

function Redstor() {
  const [status, setStatus] = useState<any>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [customers, setCustomers] = useState<SimpleCustomer[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'mapped' | 'unmapped' | 'hidden'>('all');
  const [hidden, setHidden] = useState<Set<string>>(loadHidden);
  const toggleHidden = (id: string) => setHidden(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); saveHidden(n); return n; });
  const unhideAll = () => { const n = new Set<string>(); saveHidden(n); setHidden(n); if (filter === 'hidden') setFilter('all'); };
  const [search, setSearch] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  // API key section
  const [config, setConfig] = useState<Config | null>(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [cfgKey, setCfgKey] = useState('');
  const [cfgUrl, setCfgUrl] = useState('https://api.redstor.com');
  const [cfgAuth, setCfgAuth] = useState('api_key');
  // manual entry
  const [manualOpen, setManualOpen] = useState(false);
  const [mCompany, setMCompany] = useState('');
  const [mProduct, setMProduct] = useState('Cloud Backup');
  const [mGb, setMGb] = useState('');
  const [mSeats, setMSeats] = useState('');
  const [mUsers, setMUsers] = useState('');
  const [mWorkloads, setMWorkloads] = useState('');

  const load = useCallback(async () => {
    try {
      const [s, c, cust, cfg] = await Promise.all([redstorApi.status(), redstorApi.companies(), customersApi.list(), redstorApi.getConfig().catch(() => null)]);
      setStatus(s); setRows(c.companies);
      setCustomers((cust as any[]).map(x => ({ id: x.id, xero_name: x.xero_name })).sort((a, b) => a.xero_name.localeCompare(b.xero_name)));
      if (cfg) { setConfig(cfg); setCfgUrl(cfg.url); setCfgAuth(cfg.auth || 'api_key'); }
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to load Redstor');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (key: string, fn: () => Promise<any>, ok?: (r: any) => string) => {
    setBusy(key);
    try { const r = await fn(); if (ok) toast.success(ok(r), { duration: 7000 }); await load(); return r; }
    catch (e: any) { toast.error(e.response?.data?.detail || 'Request failed', { duration: 8000 }); }
    finally { setBusy(null); }
  };

  const sync = () => act('sync', () => redstorApi.sync(),
    r => `${r.companies} companies · ${r.total_gb} GB protected · ${r.matched} mapped (${r.created_mappings} new) · ${r.billing_lines} billing lines`);
  const autoMap = () => act('automap', () => redstorApi.autoMap(), r => `${r.matched} mapped · ${r.unmatched} still unmapped`);
  const importCsv = async (f: File) => {
    await act('import', () => redstorApi.importCsv(f), r =>
      `${r.rows} rows for ${r.companies} companies imported (columns: ${Object.entries(r.columns).filter(([, v]) => v).map(([k, v]) => `${k} = ${v}`).join(', ')}) · ${r.created_mappings} auto-mapped`);
    if (fileRef.current) fileRef.current.value = '';
  };
  const saveConfig = async () => {
    if (!cfgKey.trim()) { toast.error('Paste the API key first'); return; }
    const r = await act('config', () => redstorApi.saveConfig({ api_key: cfgKey.trim(), url: cfgUrl.trim() || undefined, auth: cfgAuth }));
    if (!r) return;
    if (r.connected) {
      toast.success(r.sync ? `Key works — ${r.company}: ${r.sync.companies} companies, ${r.sync.total_gb} GB synced` : `Key works — saved${r.sync_error ? ` (sync failed: ${r.sync_error})` : ''}`, { duration: 8000 });
      setCfgKey(''); setConfigOpen(false);
    } else {
      toast.error(`Key saved but Redstor rejected it: ${r.error}`, { duration: 10000 });
    }
  };
  const forgetConfig = () => act('config', () => redstorApi.deleteConfig(), r => r.source === 'env' ? 'Stored key removed — using the .env key' : 'Stored key removed');
  const addManual = async () => {
    if (!mCompany.trim() || !mProduct.trim()) { toast.error('Company and product are required'); return; }
    const num = (s: string) => (s.trim() === '' ? undefined : Number(s));
    const r = await act('manual', () => redstorApi.manual({ company_name: mCompany.trim(), product_name: mProduct.trim(), gb: num(mGb), seats: num(mSeats), users: num(mUsers), workloads: num(mWorkloads) }),
      () => `${mCompany.trim()} · ${mProduct.trim()} saved`);
    if (r) { setMCompany(''); setMGb(''); setMSeats(''); setMUsers(''); setMWorkloads(''); }
  };
  const setMetric = (p: Product, metric: string) =>
    act(`metric${p.product_key}`, () => redstorApi.saveSettings({ bill_by: { [p.product_key]: metric } }),
      r => `${p.product_name}: billed by ${METRICS.find(m => m.v === metric)?.label.toLowerCase()} · ${r.billing_lines} lines`);
  const removeCompany = (r: Row) => {
    if (!window.confirm(`Remove ${r.name} (${r.source}) from Redstor in CloudView?\n\nIts mapping and billing lines are removed too.`)) return;
    act(`del${r.company_id}`, () => redstorApi.deleteCompany(r.company_id), () => `${r.name} removed`);
  };

  const shown = rows.filter(r => !hidden.has(r.company_id));
  const visible = useMemo(() => rows
    .filter(r => filter === 'hidden' ? hidden.has(r.company_id) : !hidden.has(r.company_id))
    .filter(r => filter === 'all' || filter === 'hidden' || (filter === 'mapped' ? !!r.customer_id : !r.customer_id))
    .filter(r => !search || `${r.name} ${r.customer_name || ''} ${r.products.map(p => p.product_name).join(' ')}`.toLowerCase().includes(search.toLowerCase())),
    [rows, filter, search, hidden]);
  const totals = useMemo(() => visible.reduce((a, r) => ({ gb: a.gb + r.total_gb, seats: a.seats + r.total_seats, billed: a.billed + (r.billed_quantity || 0) }), { gb: 0, seats: 0, billed: 0 }), [visible]);
  const mapped = shown.filter(r => r.customer_id).length;
  const lastSync = status?.last_sync;
  const keyDead = lastSync && lastSync.status !== 'success';

  return (
    <div className="redstor-page">
      <div className="page-header">
        <h1>Redstor</h1>
        <p className="page-description">Cloud backup usage per customer company. From the RedAPI, a RedApp CSV export, or typed in — map once and the bill-by figure flows into Invoice Prep.</p>
      </div>

      {status && (
        <div className="rd-strip">
          <span className={`rd-pill ${status.configured ? (keyDead ? 'bad' : 'ok') : 'warn'}`} title={lastSync?.message || ''}>
            {!status.configured ? '○ No API key — CSV / manual only'
              : keyDead ? `● API: last sync failed ${timeAgo(lastSync.finished_at)} — cached values shown`
              : lastSync ? `● API OK · synced ${timeAgo(lastSync.finished_at)}` : '● API key set — not synced yet'}
          </span>
          <span className="rd-pill">{shown.length} companies · {mapped} mapped{status.by_source ? ` · ${status.by_source.api} api / ${status.by_source.csv} csv / ${status.by_source.manual} manual` : ''}</span>
          <span className="rd-pill">{status.total_gb.toLocaleString()} GB · {status.total_seats.toLocaleString()} seats</span>
          <span className="rd-grow" />
          <button className={`btn btn-sm ${configOpen ? 'btn-primary' : 'btn-outline'}`} onClick={() => { setConfigOpen(o => !o); setManualOpen(false); }} title="Paste or replace the RedAPI key (stored encrypted in CloudView)">⚙ API key</button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={e => e.target.files?.[0] && importCsv(e.target.files[0])} />
          <button className="btn btn-sm btn-outline" disabled={busy != null} onClick={() => fileRef.current?.click()} title="Upload a CSV export from the RedApp (company / product / protected GB / seats columns are picked up by name)">📎 Import CSV</button>
          <button className={`btn btn-sm ${manualOpen ? 'btn-primary' : 'btn-outline'}`} onClick={() => { setManualOpen(o => !o); setConfigOpen(false); }}>＋ Add by hand</button>
          <button className="btn btn-sm btn-outline" disabled={busy != null} onClick={autoMap}>✨ Auto-map by name</button>
          <button className="btn btn-sm btn-primary" disabled={busy === 'sync' || !status.configured} onClick={sync}>{busy === 'sync' ? '⏳ Syncing…' : '🔄 Sync from Redstor'}</button>
        </div>
      )}

      {configOpen && (
        <div className="rd-panel">
          <div className="rd-panel-head">
            <strong>Redstor RedAPI key</strong>
            <span className="rd-panel-src">
              {config?.source === 'stored' ? `Stored in CloudView (key ${config.token_hint}${config.saved_at ? `, saved ${timeAgo(config.saved_at)}` : ''})`
                : config?.source === 'env' ? `Using the server .env key (${config.token_hint}) — paste one here to override it`
                : 'No key yet — a Partner Admin creates one in the RedApp (service account / API client)'}
            </span>
          </div>
          <div className="rd-row">
            <label>API base URL<input className="input w-url" value={cfgUrl} onChange={e => setCfgUrl(e.target.value)} placeholder="https://api.redstor.com" /></label>
            <label>Sent as
              <select value={cfgAuth} onChange={e => setCfgAuth(e.target.value)} title="X-Api-Key is what the public RedAPI spec uses; Bearer is the OAuth token from the service-account flow">
                <option value="api_key">X-Api-Key header</option>
                <option value="bearer">Authorization: Bearer</option>
              </select>
            </label>
            <label>API key<input className="input w-key" type="password" value={cfgKey} onChange={e => setCfgKey(e.target.value)} placeholder="Paste the key / token" onKeyDown={e => { if (e.key === 'Enter') saveConfig(); }} autoFocus /></label>
            <button className="btn btn-sm btn-primary" onClick={saveConfig} disabled={busy != null || !cfgKey.trim()}>{busy === 'config' ? 'Saving & syncing…' : 'Save, test & sync'}</button>
            {config?.source === 'stored' && <button className="btn btn-sm btn-outline" onClick={forgetConfig} disabled={busy != null}>Forget stored key</button>}
          </div>
          <p className="rd-hint">The key is encrypted at rest and only reads companies and consumption. A failed sync never touches the cached companies, mappings or quantities.</p>
        </div>
      )}

      {manualOpen && (
        <div className="rd-panel">
          <div className="rd-panel-head"><strong>Add or update a company by hand</strong><span className="rd-panel-src">Same company + product again = update the figures. Leave a figure blank to keep it.</span></div>
          <div className="rd-row">
            <label>Company<input className="input w-url" list="rd-company-names" value={mCompany} onChange={e => setMCompany(e.target.value)} placeholder="Customer company name" /></label>
            <datalist id="rd-company-names">{rows.map(r => <option key={r.company_id} value={r.name} />)}</datalist>
            <label>Product<input className="input w-url" list="rd-product-names" value={mProduct} onChange={e => setMProduct(e.target.value)} /></label>
            <datalist id="rd-product-names">{Array.from(new Set(rows.flatMap(r => r.products.map(p => p.product_name || '')))).filter(Boolean).map(n => <option key={n} value={n} />)}</datalist>
            <label>GB protected<input className="input w-num" value={mGb} onChange={e => setMGb(e.target.value)} placeholder="e.g. 250" /></label>
            <label>Seats<input className="input w-num" value={mSeats} onChange={e => setMSeats(e.target.value)} /></label>
            <label>Users<input className="input w-num" value={mUsers} onChange={e => setMUsers(e.target.value)} /></label>
            <label>Workloads<input className="input w-num" value={mWorkloads} onChange={e => setMWorkloads(e.target.value)} /></label>
            <button className="btn btn-sm btn-primary" onClick={addManual} disabled={busy != null}>Save</button>
          </div>
        </div>
      )}

      <div className="rd-toolbar">
        {(['all', 'mapped', 'unmapped'] as const).map(f => (
          <button key={f} className={`rd-chip ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>{f}</button>
        ))}
        {hidden.size > 0 && (
          <>
            <button className={`rd-chip ${filter === 'hidden' ? 'active' : ''}`} onClick={() => setFilter(filter === 'hidden' ? 'all' : 'hidden')}>🙈 Hidden ({hidden.size})</button>
            {filter === 'hidden' && <button className="rd-chip" onClick={unhideAll}>Unhide all</button>}
          </>
        )}
        <input className="rd-search" placeholder="Search companies / products…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <table className="rd-table">
        <thead>
          <tr>
            <th>Redstor company</th>
            <th>Products · figures · billed by</th>
            <th className="num">GB</th>
            <th className="num">Seats</th>
            <th className="num" title="Sum of the bill-by quantities on this company's billing lines">Billed qty</th>
            <th>CloudView customer</th>
            <th>Synced</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {visible.length === 0 && <tr><td colSpan={8} className="rd-empty">{rows.length === 0 ? 'Nothing yet. Paste the API key and sync, import a CSV export, or add a company by hand.' : 'Nothing matches.'}</td></tr>}
          {visible.map(r => (
            <tr key={r.company_id} className={`${r.customer_id ? 'mapped' : 'unmapped'} ${r.in_console ? '' : 'gone'} ${r.customer_id && r.billed_quantity === 0 ? 'no-qty' : ''}`}
                title={r.customer_id && r.billed_quantity === 0 ? 'Nothing to bill for this company (no protected data / seats) - no lines in Invoice Prep' : undefined}>
              <td>
                <div className="rd-name">
                  {r.name}
                  {r.source !== 'api' && <span className={`rd-tag ${r.source}`}>{r.source}</span>}
                  {r.source === 'api' && !r.in_console && <span className="rd-tag warn" title="Not returned by the last successful sync">gone</span>}
                  {r.customer_id && r.billed_quantity === 0 && <span className="rd-tag danger" title="Bill-by figure is 0 on every product">0 qty · not billed</span>}
                </div>
                <div className="rd-muted">{r.company_id}</div>
              </td>
              <td>
                <div className="rd-products">
                  {r.products.length === 0 && <span className="rd-muted">no products yet</span>}
                  {r.products.map(p => (
                    <div key={p.product_key} className="rd-product">
                      <span>{p.product_name}{p.is_trial ? <span className="rd-tag">trial</span> : null}</span>
                      <span className="rd-figs"><b>{p.gb.toLocaleString()}</b> GB · <b>{p.seats}</b> seats · <b>{p.users}</b> users · <b>{p.workloads}</b> wl</span>
                      <select value={p.metric} disabled={busy != null} onChange={e => setMetric(p, e.target.value)} title="What this product is billed on (applies to every company)">
                        {METRICS.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
                      </select>
                      <span className="rd-qty" title="Quantity on the billing line">{p.metric === 'none' ? '—' : p.quantity}</span>
                    </div>
                  ))}
                </div>
              </td>
              <td className="num">{r.total_gb.toLocaleString()}</td>
              <td className="num">{r.total_seats}</td>
              <td className="num"><strong>{r.billed_quantity == null ? '—' : r.billed_quantity}</strong></td>
              <td className="rd-map-cell">
                <CustomerPicker value={r.customer_id} valueName={r.customer_name} customers={customers} disabled={busy != null}
                                onSelect={id => act(`m${r.company_id}`, () => redstorApi.map(r.company_id, id), () => `Mapped ${r.name}`)}
                                onClear={() => act(`u${r.company_id}`, () => redstorApi.unmap(r.company_id), () => `Unmapped ${r.name}`)} />
              </td>
              <td className="rd-muted" title={r.last_synced || ''}>{timeAgo(r.last_synced)}</td>
              <td className="rd-hide">
                <button className="rd-hide-btn" onClick={() => toggleHidden(r.company_id)} title={hidden.has(r.company_id) ? 'Unhide' : 'Hide from the list and totals (this browser)'}>{hidden.has(r.company_id) ? '👁' : '🙈'}</button>
                {r.source !== 'api' && <button className="rd-hide-btn" onClick={() => removeCompany(r)} title="Remove this CSV / manual company">🗑</button>}
              </td>
            </tr>
          ))}
        </tbody>
        {visible.length > 0 && (
          <tfoot>
            <tr>
              <td colSpan={2}>{visible.length} companies</td>
              <td className="num">{totals.gb.toLocaleString()}</td>
              <td className="num">{totals.seats}</td>
              <td className="num">{totals.billed}</td>
              <td colSpan={3} className="rd-muted">{hidden.size > 0 && filter !== 'hidden' ? `${hidden.size} hidden` : ''}</td>
            </tr>
          </tfoot>
        )}
      </table>
      <p className="rd-hint">
        Each mapped product becomes one <strong>Redstor &lt;product&gt; - &lt;billed by&gt;</strong> line in Invoice Prep with the figure as quantity.
        Set the selling price once on the Price book (service type redstor). "Billed by" is per product and applies to every company;
        default: seats for Microsoft 365 / Google style products, GB for everything else.
      </p>
    </div>
  );
}

export default Redstor;
